import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';

import { createUser, getAuth } from '../../api/auth';
import { serviceAvailable } from '../../api/serviceAvailable';
import { getPostDetails } from '../../api/social';
import { getSubscriptionInfo } from '../../api/subscription';

import { sendGTMEvent } from '../../utils/analytics';
import {
  cloudGetItem,
  localGetItem,
  localSetItem,
} from '../../utils/cloudStorage';
import {
  AVATAR_URL,
  CLOUD_STORAGE_KEYS,
  GA4_KEY,
  ROUTES,
} from '../../utils/constants';

import { useApp } from '../../context/AppContext';
import { useProduct } from '../../context/ProductContext';
import { useSubscription } from '../../context/SubscriptionContext';

// import CachedImage from '../CachedImage/CachedImage';

import styles from './Loading.module.css';
import { colors } from '../../styles/colors';

const MESSAGES = {
  INIT: 'Загрузка приложения...',
  AUTH: 'Загрузка приложения...',
  LOADING: 'Загрузка приложения...',
  FINALIZING: 'Загрузка приложения...',
};

const Loading = ({
  setIsLoading,
  setIsPopupOpen,
  showOffert,
  setShowOffert,
}) => {
  const navigate = useNavigate();

  const {
    setEntranceInfo,
    user,
    telegram,
    basicDataLoaded,
    criticalFilesLoadedProgress,
  } = useApp();
  const { setProductsDetails } = useProduct();
  const { setSubscriptionInfo } = useSubscription();

  const [appProgress, setAppProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(MESSAGES.INIT);
  const [error, setError] = useState(null);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);

  // анимация

  const checkAvailability = (available) => {
    if (!available || (!available.service_available && available.user_exists)) {
      navigate(ROUTES.notAvailable);
    }
  };

  const updateProgress = (newProgress, message) => {
    setAppProgress((prev) => Math.max(prev, newProgress));
    if (message) setCurrentMessage(message);
  };

  // Основная инициализация приложения
  useEffect(() => {
    const initializeApp = async () => {
      let available_info = null;

      updateProgress(10, MESSAGES.INIT);
      if (telegram) {
        telegram.setHeaderColor(colors.background.main);
        telegram.expand();
        telegram.disableVerticalSwipes();
      }

      // запрос на доступ к приложению
      try {
        const available = await serviceAvailable();
        available_info = available;
        setEntranceInfo(available);
        checkAvailability(available_info);
      } catch (error) {
        console.error('Error fetching service availability:', error);
        navigate(ROUTES.notAvailable);
      }

      if (!available_info) {
        setIsLoading(false);
        return;
      }

      try {
        // проверка подтвержения принятия соглашения
        const isOfferConfirmed = await cloudGetItem(
          CLOUD_STORAGE_KEYS.is_offer_confirmed
        ).catch(() => null);

        if (isOfferConfirmed && !showOffert) {
          setIsPopupOpen(false);
          setShowOffert(false);
        } else {
          setIsPopupOpen(true);
          setShowOffert(true);
        }

        let token = localGetItem('Token');

        updateProgress(30, MESSAGES.AUTH);
        if (!token) {
          const authData = await getAuth(user);
          token = authData.token;
          localSetItem('Token', token, 3600 * 24);
        }

        updateProgress(40, MESSAGES.AUTH);

        if (available_info && !available_info?.user_exists) {
          const data = await createUser(user);
          available_info = data;
          setEntranceInfo(data);
          checkAvailability(available_info);
        }

        updateProgress(60);

        updateProgress(70, MESSAGES.LOADING);
        const secondaryDataPromises = Promise.allSettled([
          getPostDetails(),
          getSubscriptionInfo(),
        ]);

        updateProgress(90, MESSAGES.FINALIZING);
        const secondaryResults = await secondaryDataPromises;

        secondaryResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            if (index === 0) setProductsDetails(result.value || []);
            if (index === 1) setSubscriptionInfo(result.value || {});
          } else {
            console.warn(`Secondary data loading error: ${result.reason}`);
            if (index === 0) setProductsDetails([]);
            if (index === 1) setSubscriptionInfo({});
          }
        });

        updateProgress(100, MESSAGES.FINALIZING);
        setIsFullyLoaded(true);

        // setTimeout(() => setIsLoading(false), 200);
      } catch (error) {
        console.error('App initialization error:', error);
        setError('Ошибка загрузки приложения');
        setIsLoading(false);
      }
    };

    const initGA = () => {
      const ga_key = process.env.REACT_APP_GOOGLE_ANALYTICS_KEY;
      if (ga_key) {
        ReactGA.initialize(ga_key);
      }
      sendGTMEvent(GA4_KEY.loading);
    };

    initGA();
    initializeApp();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    let timeoutId;
    if (isFullyLoaded && basicDataLoaded) {
      timeoutId = setTimeout(() => setIsLoading(false), 200);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    //eslint-disable-next-line
  }, [isFullyLoaded, basicDataLoaded]);

  return (
    <div className={styles.playScreen}>
      <div className={styles.version}>v1.0.0</div>
      <img
        className={styles.haloImage}
        src={AVATAR_URL(window.APP_ID)}
        alt={(window.APP_NAME || 'App logo')}
      />

      <h1 className={styles.haloTitle}>{window.APP_NAME || ''}</h1>

      <div className={styles.loadingContainer}>
        <div className={styles.loadingBar}>
          <div
            className={styles.loadingProgress}
            style={{
              width: `${
                appProgress * 0.8 + criticalFilesLoadedProgress * 0.2
              }%`,
              transition: 'width 0.3s ease-out',
            }}
          />
        </div>

        <div className={styles.loadingText}>
          {error || (isFullyLoaded ? 'Старт...' : currentMessage)}
        </div>
      </div>
      <svg
        className={styles.partnerLogo}
        width='85'
        height='85'
        viewBox='0 0 85 85'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <rect width='85' height='85' rx='12' fill='white' />
        <path
          d='M43 20V11.5L36.5 12L30.5 15.5L24.5 22L22.5 29.5L23.5 38.5L27.5 44.5L35 50.5L43 51.5V43H39L35 40.5L32 38L31 32L32 26.5L36 21.5L43 20Z'
          fill='url(#paint0_linear_325_114)'
        />
        <path
          d='M43 43L43 51.5L49.5 51L55.5 47.5L61.5 41L63.5 33.5L62.5 24.5L58.5 18.5L51 12.5L43 11.5L43 20L47 20L51 22.5L54 25L55 31L54 36.5L50 41.5L43 43Z'
          fill='url(#paint1_linear_325_114)'
        />
        <circle
          cx='42.5'
          cy='31.5'
          r='20'
          transform='rotate(180 42.5 31.5)'
          stroke='#132138'
          strokeWidth='3'
        />
        <path
          d='M30.5 31.5C30.5 24.8726 35.8726 19.5 42.5 19.5C49.1274 19.5 54.5 24.8726 54.5 31.5C54.5 38.1274 49.1274 43.5 42.5 43.5C35.8726 43.5 30.5 38.1274 30.5 31.5Z'
          fill='url(#paint2_linear_325_114)'
          stroke='#132138'
          strokeWidth='3'
        />
        <path
          d='M15.7972 74C15.6427 74 15.5162 73.9508 15.4179 73.8525C15.3195 73.7541 15.2703 73.6277 15.2703 73.4731V59.7739C15.2703 59.6194 15.3195 59.4929 15.4179 59.3946C15.5162 59.2962 15.6427 59.2471 15.7972 59.2471H18.516C18.6705 59.2471 18.797 59.2962 18.8953 59.3946C18.9937 59.4929 19.0429 59.6194 19.0429 59.7739V64.8532H24.2485V59.7739C24.2485 59.6194 24.2977 59.4929 24.3961 59.3946C24.4944 59.2962 24.6209 59.2471 24.7754 59.2471H27.4942C27.6488 59.2471 27.7752 59.2962 27.8736 59.3946C27.9719 59.4929 28.0211 59.6194 28.0211 59.7739V73.4731C28.0211 73.6277 27.9719 73.7541 27.8736 73.8525C27.7752 73.9508 27.6488 74 27.4942 74H24.7754C24.6209 74 24.4944 73.9508 24.3961 73.8525C24.2977 73.7541 24.2485 73.6277 24.2485 73.4731V68.2253H19.0429V73.4731C19.0429 73.6277 18.9937 73.7541 18.8953 73.8525C18.797 73.9508 18.6705 74 18.516 74H15.7972ZM30.1311 74C30.0187 74 29.9133 73.9578 29.815 73.8735C29.7307 73.7752 29.6885 73.6698 29.6885 73.5574C29.6885 73.4872 29.6956 73.4239 29.7096 73.3677L34.6624 59.8372C34.7045 59.6826 34.7888 59.5491 34.9153 59.4367C35.0558 59.3103 35.2455 59.2471 35.4843 59.2471H38.6035C38.8424 59.2471 39.025 59.3103 39.1515 59.4367C39.292 59.5491 39.3833 59.6826 39.4255 59.8372L44.3572 73.3677C44.3853 73.4239 44.3993 73.4872 44.3993 73.5574C44.3993 73.6698 44.3501 73.7752 44.2518 73.8735C44.1675 73.9578 44.0621 74 43.9357 74H41.3434C41.1326 74 40.971 73.9508 40.8586 73.8525C40.7603 73.7401 40.697 73.6417 40.6689 73.5574L39.847 71.4077H34.2198L33.4189 73.5574C33.3908 73.6417 33.3276 73.7401 33.2292 73.8525C33.1309 73.9508 32.9623 74 32.7234 74H30.1311ZM35.0839 68.3728H39.004L37.0229 62.8088L35.0839 68.3728ZM46.5874 74C46.4328 74 46.3064 73.9508 46.208 73.8525C46.1097 73.7541 46.0605 73.6277 46.0605 73.4731V59.7739C46.0605 59.6194 46.1097 59.4929 46.208 59.3946C46.3064 59.2962 46.4328 59.2471 46.5874 59.2471H49.3694C49.5099 59.2471 49.6293 59.2962 49.7277 59.3946C49.8401 59.4929 49.8963 59.6194 49.8963 59.7739V70.8176H56.2611C56.4157 70.8176 56.5421 70.8738 56.6405 70.9862C56.7529 71.0845 56.8091 71.211 56.8091 71.3655V73.4731C56.8091 73.6277 56.7529 73.7541 56.6405 73.8525C56.5421 73.9508 56.4157 74 56.2611 74H46.5874ZM64.4989 74.2108C63.2062 74.2108 62.0822 74 61.1268 73.5785C60.1714 73.157 59.4267 72.5247 58.8928 71.6817C58.3589 70.8246 58.0638 69.7568 58.0076 68.4782C57.9935 67.874 57.9865 67.2698 57.9865 66.6657C57.9865 66.0615 57.9935 65.4503 58.0076 64.8321C58.0638 63.5676 58.3589 62.5068 58.8928 61.6497C59.4407 60.7786 60.1924 60.1252 61.1479 59.6896C62.1033 59.2541 63.2203 59.0363 64.4989 59.0363C65.7634 59.0363 66.8734 59.2541 67.8288 59.6896C68.7983 60.1252 69.557 60.7786 70.105 61.6497C70.653 62.5068 70.948 63.5676 70.9902 64.8321C71.0183 65.4503 71.0323 66.0615 71.0323 66.6657C71.0323 67.2698 71.0183 67.874 70.9902 68.4782C70.934 69.7568 70.6389 70.8246 70.105 71.6817C69.5711 72.5247 68.8264 73.157 67.871 73.5785C66.9156 74 65.7915 74.2108 64.4989 74.2108ZM64.4989 71.1759C65.2436 71.1759 65.8477 70.9511 66.3114 70.5014C66.7891 70.0518 67.042 69.3353 67.0701 68.3517C67.0982 67.7335 67.1123 67.1504 67.1123 66.6025C67.1123 66.0545 67.0982 65.4854 67.0701 64.8953C67.042 64.235 66.9156 63.701 66.6908 63.2936C66.48 62.8721 66.1849 62.563 65.8056 62.3663C65.4262 62.1695 64.9907 62.0712 64.4989 62.0712C64.0212 62.0712 63.5926 62.1695 63.2133 62.3663C62.8339 62.563 62.5318 62.8721 62.307 63.2936C62.0822 63.701 61.9558 64.235 61.9277 64.8953C61.9136 65.4854 61.9066 66.0545 61.9066 66.6025C61.9066 67.1504 61.9136 67.7335 61.9277 68.3517C61.9698 69.3353 62.2227 70.0518 62.6864 70.5014C63.15 70.9511 63.7542 71.1759 64.4989 71.1759Z'
          fill='#132138'
        />
        <defs>
          <linearGradient
            id='paint0_linear_325_114'
            x1='32.75'
            y1='11.5'
            x2='32.75'
            y2='51.5'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopColor='#A0FDFF' />
            <stop offset='1' stopColor='#629BFE' />
          </linearGradient>
          <linearGradient
            id='paint1_linear_325_114'
            x1='53.25'
            y1='11.5'
            x2='53.25'
            y2='51.5'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopColor='#FDEE75' />
            <stop offset='1' stopColor='#FF5F06' />
          </linearGradient>
          <linearGradient
            id='paint2_linear_325_114'
            x1='42.5'
            y1='18'
            x2='42.5'
            y2='45'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopColor='#FFD468' />
            <stop offset='1' stopColor='#FF8521' />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export default Loading;
